import { useEffect, useState } from 'react'
import type { CreateTareaDTO, TareaDTO, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { dueView, enumLabel, errorMessage, formatSpokenDateTime, isClosed, personName, PRIORITY_STYLES, TYPE_ICONS } from '../common/task-common'

export interface TaskDetailDrawerProps {
    tareaId: number | null
    onClose: () => void
    onEdit?: (tareaId: number) => void
    onChanged?: (tarea: TareaDTO) => void
}

const LOAD_ERROR = 'No se pudo cargar el detalle de la tarea.'
const SAVE_ERROR = 'No se pudo actualizar la tarea.'

const toRequest = (tarea: TareaDTO, status: 'abierta' | 'cerrada'): CreateTareaDTO => ({
    id: tarea.id,
    nombre: tarea.nombre || '',
    descripcion: tarea.descripcion || '',
    vencimiento: tarea.vencimiento,
    prioridad: tarea.prioridad?.name || 'media',
    tipo: tarea.tipo?.name || 'tarea',
    vendedor: tarea.vendedor?.id ?? null,
    status,
})

export const useTaskDetailDrawerModel = ({ tareaId, onChanged }: TaskDetailDrawerProps) => {
    const [tarea, setTarea] = useState<TareaDTO | null>(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reloadToken, setReloadToken] = useState(0)

    useEffect(() => {
        if (!tareaId) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                setTarea(null)
                const payload: WrapperResponse<TareaDTO> | null = await (await APIInvoke.invokeGET(`/tareas/${tareaId}`)).json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setTarea(payload.body)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [tareaId, reloadToken])

    const toggleStatus = async () => {
        if (!tarea || saving) return
        try {
            setSaving(true)
            setError(null)
            const next = isClosed(tarea.status) ? 'abierta' : 'cerrada'
            const payload: WrapperResponse<TareaDTO> = await (await APIInvoke.invokePUT(`/tareas/${tarea.id}`, toRequest(tarea, next))).json()
            if (!payload.ok || !payload.body) throw new Error(payload.message || SAVE_ERROR)
            setTarea(payload.body)
            onChanged?.(payload.body)
        } catch (saveError) {
            setError(errorMessage(saveError, SAVE_ERROR))
        } finally {
            setSaving(false)
        }
    }

    const closed = isClosed(tarea?.status)
    const prioridad = tarea?.prioridad?.name

    return {
        tarea,
        loading,
        saving,
        error,
        closed,
        TypeIcon: TYPE_ICONS[tarea?.tipo?.name || 'tarea'] || TYPE_ICONS.tarea,
        tipo: enumLabel(tarea?.tipo),
        prioridad: enumLabel(tarea?.prioridad),
        prioridadClass: (prioridad && PRIORITY_STYLES[prioridad]) || 'bg-k-line-soft text-k-ink-2',
        status: enumLabel(tarea?.status),
        due: dueView(tarea?.vencimiento, tarea?.status),
        creacion: formatSpokenDateTime(tarea?.fecha) || '—',
        cierre: formatSpokenDateTime(tarea?.fechaCierre) || '',
        vendedor: tarea?.vendedor ? personName(tarea.vendedor) : '',
        toggleStatus,
        reload: () => setReloadToken((token) => token + 1),
    }
}
